import React, {useEffect, useMemo, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {toastError} from "../../../helpers/Toast";

const formatBytes = value => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
};

const errorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Nie udało się pobrać dokumentów z folderu pracownika.";

const EmployeeFolderDocumentsCard = ({employeeId = null, title = null}) => {
  const axiosPrivate = useAxiosPrivate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [preview, setPreview] = useState(null);
  const baseUrl = useMemo(() => employeeId ? `/employees/${employeeId}/folder-documents` : "/employee-folder-documents", [employeeId]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await axiosPrivate.get(baseUrl);
        if (active) setDocuments(response.data || []);
      } catch (error) {
        if (active) {
          setDocuments([]);
          setLoadError(errorMessage(error));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [axiosPrivate, baseUrl]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const showPreview = async item => {
    try {
      const response = await axiosPrivate.get(`${baseUrl}/preview`, {
        params: {path: item.path},
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      setPreview({url, fileName: item.fileName});
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const download = async item => {
    try {
      const response = await axiosPrivate.get(`${baseUrl}/download`, {
        params: {path: item.path},
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = item.fileName || "dokument";
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  return (
    <>
      <div className="card shadow-sm mt-3">
        <div className="card-header">
          <strong>{title || (employeeId ? "Dokumenty z folderu pracownika" : "Dokumenty w moim folderze pracowniczym")}</strong>
          <div className="small text-muted mt-1">Pliki zapisane w katalogu pracownika na serwerze, w tym wygenerowane umowy z podfolderu Umowy.</div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-muted py-2">Ładowanie dokumentów…</div>
          ) : loadError ? (
            <div className="alert alert-warning mb-0">{loadError}</div>
          ) : documents.length === 0 ? (
            <div className="text-muted py-2">Brak plików w folderze pracownika.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Katalog</th>
                    <th>Plik</th>
                    <th>Rozmiar</th>
                    <th>Zmodyfikowano</th>
                    <th className="text-end">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(item => (
                    <tr key={item.path}>
                      <td>{item.folder || "Główny katalog"}</td>
                      <td>{item.fileName}</td>
                      <td>{formatBytes(item.fileSize)}</td>
                      <td>{formatDateTime(item.modifiedAt)}</td>
                      <td className="text-end text-nowrap">
                        {item.previewAvailable && (
                          <Button type="button" size="sm" variant="outline-secondary" className="me-2" onClick={() => showPreview(item)}>
                            Podgląd
                          </Button>
                        )}
                        <Button type="button" size="sm" variant="outline-primary" onClick={() => download(item)}>
                          Pobierz
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={!!preview} onHide={closePreview} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{preview?.fileName || "Podgląd dokumentu"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {preview?.url && <iframe src={preview.url} title={preview.fileName || "Podgląd dokumentu"} style={{width: "100%", height: "75vh", border: 0}} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreview}>Zamknij</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmployeeFolderDocumentsCard;
